import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, AlertTriangle } from "lucide-react";
import { useSubscription } from "../hooks/useSubscription";
import { useNotification } from "../contexts/NotificationContext";

import UpgradePrompt from "../components/UpgradePrompt";
import { getApiUrl } from "../config/api";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function FamilyManagement() {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactRelationship, setNewContactRelationship] = useState("child");
  const [newContactNotes, setNewContactNotes] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  
  const { hasFeature, getTierName, getMaxFamilyMembers, canAddFamilyMember, isLoading: subscriptionLoading } = useSubscription();
  const { showPasswordSuccess } = useNotification();

  const fetchContacts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/settings/family-members"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(response.data || []);
    } catch (error) {
      console.error("Error fetching family contacts:", error);
      setError("Failed to load family contacts");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFeature('family_plan')) {
      fetchContacts();
    } else {
      setIsLoading(false);
    }
  }, [hasFeature, fetchContacts]);

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!newContactName.trim() || isProcessing) return;

    setIsProcessing(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      await axios.post(getApiUrl("/api/settings/family-members"), {
        name: newContactName,
        relationship: newContactRelationship,
        notes: newContactNotes
      }, { headers: { Authorization: `Bearer ${token}` } });

      setNewContactName("");
      setNewContactRelationship("child");
      setNewContactNotes("");
      setShowAddForm(false);
      await fetchContacts();
      showPasswordSuccess("Family contact added successfully!", 5000);
    } catch (err) {
      console.error("Error adding family contact:", err);
      setError(err.response?.data?.error || "Failed to add family contact");
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmRemoveContact = (contact) => {
    setDeletingContact(contact);
    setShowDeleteConfirm(true);
  };

  const handleRemoveContact = async () => {
    if (!deletingContact) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${getApiUrl("/api/settings/family-members")}/${deletingContact.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchContacts();
      showPasswordSuccess("Family contact removed successfully!", 5000);
    } catch (err) {
      console.error("Error removing family contact:", err);
      setError("Failed to remove family contact");
    } finally {
      setShowDeleteConfirm(false);
      setDeletingContact(null);
    }
  };

  // Wait for subscription data to load before making any decisions
  if (subscriptionLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!hasFeature('family_plan')) {
    return (
      <UpgradePrompt 
        featureName="family_plan" 
        currentTier={getTierName()} 
        onClose={() => window.history.back()}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="mr-2 text-black-500" /> Family Management
          </h1>
          <p className="text-gray-600 mt-2">Manage family members who have access to your account</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded m-6">
            {error}
          </div>
        )}

        {/* Family Plan Info */}
        <div className="p-6 border-b border-gray-200">
          <div className="bg-blue-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-blue-900 mb-2">Family Plan Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Current Members:</span>
                <span className="ml-2 text-blue-800">{contacts.length}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Max Members:</span>
                <span className="ml-2 text-blue-800">
                  {getMaxFamilyMembers() === -1 ? "Unlimited" : getMaxFamilyMembers()}
                </span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Plan:</span>
                <span className="ml-2 text-blue-800">{getTierName()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Add Family Contact Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Family Contacts</h2>
            {canAddFamilyMember(contacts.length) && (
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-[#51380d] text-white px-4 py-2 rounded-md hover:bg-[#3d2a0a] transition-colors"
              >
                {showAddForm ? "Cancel" : "Add Member"}
              </button>
            )}
          </div>

          {!canAddFamilyMember(contacts.length) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800">
                You've reached the maximum number of family members for your plan. 
                <a href="/subscription" className="text-blue-600 hover:underline ml-1">
                  Upgrade to add more members.
                </a>
              </p>
            </div>
          )}

          {showAddForm && (
            <form onSubmit={handleAddContact} className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newContactName}
                    onChange={(e) => setNewContactName(e.target.value)}
                    placeholder="Enter contact's full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship
                  </label>
                  <select
                    value={newContactRelationship}
                    onChange={(e) => setNewContactRelationship(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="child">Child</option>
                    <option value="spouse">Spouse</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                  <input
                    type="text"
                    value={newContactNotes}
                    onChange={(e) => setNewContactNotes(e.target.value)}
                    placeholder="Any notes about this contact"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-4 flex space-x-3">
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="bg-[#51380d] text-white px-4 py-2 rounded-md hover:bg-[#3d2a0a] disabled:opacity-50"
                >
                  {isProcessing ? "Adding..." : "Add Contact"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewContactName("");
                    setNewContactRelationship("child");
                    setNewContactNotes("");
                  }}
                  className="bg-gray-300 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Family Contacts List */}
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No family members added yet.</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add family members to give them access to your horse management data.
                </p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {contact.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{contact.name}</h3>
                      {contact.relationship && (
                        <p className="text-sm text-gray-600 capitalize">{contact.relationship}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        Added on {formatDate(contact.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => confirmRemoveContact(contact)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className="p-6 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">How Family Plans Work</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Shared Access</h4>
              <ul className="space-y-1">
                <li>• Horses can be assigned and tracked by family member</li>
                <li>• Entries and Results can be assigned and tracked by family member</li>
                <li>• Analytics can be viewed by family member</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Remove Family Contact</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to remove <span className="font-semibold">"{deletingContact.name}"</span> from your family contacts? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingContact(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveContact}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Remove Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 